import { useSyncExternalStore } from "react";

import { DegradedBanner } from "../app/DegradedBanner";
import { StreamBadge, Topbar, TopbarClock } from "../app/Topbar";
import { useStreamContext } from "../app/StreamProvider";
import { PostSelector } from "../posts/PostSelector";
import { NetworkHealthView } from "./NetworkHealthView";
import { Thermometer } from "./Thermometer";
import { SynopticView } from "./SynopticView";
import { JunctionsView } from "./JunctionsView";
import { RegRoutesView } from "./RegRoutesView";

/**
 * Écran « Réseau » (F3) — regroupe les vues du CDC non illustrées par les
 * maquettes : thermomètre (§5.3), synoptique (§5.4), carrefours (§5.1),
 * itinéraires de régulation (§5.2). Toutes en lecture seule.
 * (Croisement §5.6 et retournement §5.7 vivent dans le cockpit, 3ᵉ colonne.)
 */
// Ordre d'affichage des onglets (demande utilisateur — « Santé » déplacée en
// fin de liste et renommée « Déviations », le contenu de la vue ne change pas :
// santé des lignes + alertes réseau, cf. `NetworkHealthView`).
const VIEWS = [
  { id: "thermometre", label: "Thermomètre", render: () => <Thermometer /> },
  { id: "synoptique", label: "Synoptique", render: () => <SynopticView /> },
  { id: "carrefours", label: "Carrefours", render: () => <JunctionsView /> },
  { id: "itineraires", label: "Itinéraires", render: () => <RegRoutesView /> },
  { id: "deviations", label: "Déviations", render: () => <NetworkHealthView /> },
] as const;

type ViewId = (typeof VIEWS)[number]["id"];
// L'onglet par défaut suit désormais l'ordre d'affichage (le premier), plutôt
// qu'un id figé — `#/reseau` seul atterrit donc sur Thermomètre, plus Déviations
// (déplacée en fin de liste). À confirmer si un autre onglet doit rester le
// point d'entrée par défaut malgré son nouveau rang visuel.
const DEFAULT_VIEW: ViewId = VIEWS[0].id;

function currentView(): ViewId {
  const seg = window.location.hash.replace(/^#\/reseau\/?/, "").split("/")[0];
  return (VIEWS.find((v) => v.id === seg)?.id ?? DEFAULT_VIEW) as ViewId;
}

function useNetworkView(): ViewId {
  return useSyncExternalStore(
    (cb) => {
      window.addEventListener("hashchange", cb);
      return () => window.removeEventListener("hashchange", cb);
    },
    currentView,
    () => DEFAULT_VIEW,
  );
}

export function NetworkScreen() {
  const { status } = useStreamContext();
  const view = useNetworkView();
  const active = VIEWS.find((v) => v.id === view) ?? VIEWS[0];

  return (
    <div className="mae-app">
      <Topbar
        current="network"
        right={
          <>
            <StreamBadge status={status} />
            <TopbarClock />
            <span style={{ filter: "invert(1)" }}>
              <PostSelector compact />
            </span>
          </>
        }
      />
      <DegradedBanner />

      <div className="tabs is-boxed" style={{ margin: 0, background: "var(--sentinelle-surface)", borderBottom: "1px solid var(--sentinelle-rule)" }}>
        <ul>
          {VIEWS.map((v) => (
            <li key={v.id} className={v.id === view ? "is-active" : ""}>
              <a className="ovl" href={`#/reseau/${v.id}`}>
                {v.label}
              </a>
            </li>
          ))}
        </ul>
      </div>

      <div className="mae-fill mae-scroll-y" style={{ padding: "16px 20px", background: "var(--sentinelle-surface)" }}>
        {active.render()}
      </div>
    </div>
  );
}
